/**
 * Heading extraction utilities shared by DocumentToc and OverviewRuler.
 * Works from raw markdown string � no DOM required.
 */

export interface DocHeading {
  /** 1, 2, or 3 (h1/h2/h3) */
  level: 1 | 2 | 3
  /** Display text of the heading */
  text: string
  /** Slugified id that matches the `id` attribute added to rendered heading elements */
  id: string
  /**
   * Proportional scroll position (0�1) relative to the scroll container's scrollHeight.
   * This is set to 0 at parse time and must be populated after DOM render via
   * `computeHeadingRatios()`.
   */
  ratio: number
}

/** Convert heading text to a lowercase hyphenated slug suitable for HTML `id` attributes. */
export function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
}

/**
 * Extract all h1�h3 headings from a raw markdown string.
 * Ratios are initialised to 0 � call `computeHeadingRatios` after DOM render.
 */
export function extractHeadings(markdown: string): DocHeading[] {
  const headings: DocHeading[] = []
  for (const line of markdown.split('\n')) {
    const match = line.match(/^(#{1,3})\s+(.+)$/)
    if (match) {
      const level = match[1].length as 1 | 2 | 3
      const text = match[2].trim()
      headings.push({ level, text, id: slugifyHeading(text), ratio: 0 })
    }
  }
  return headings
}

/**
 * Walk the rendered content DOM and fill in `ratio` for each heading.
 *
 * @param headings  Array from `extractHeadings` (mutated in place)
 * @param contentEl The element that contains the rendered markdown
 * @param scrollEl  The scrollable container (provides `scrollHeight`)
 */
export function computeHeadingRatios(
  headings: DocHeading[],
  contentEl: HTMLElement,
  scrollEl: HTMLElement
): DocHeading[] {
  const totalHeight = scrollEl.scrollHeight
  if (totalHeight === 0) return headings
  return headings.map((h) => {
    const el = contentEl.querySelector(`[id="${h.id}"]`) as HTMLElement | null
    return { ...h, ratio: el ? el.offsetTop / totalHeight : h.ratio }
  })
}

/**
 * Given a scroll position, return the id of the heading currently "active"
 * (the last heading whose `offsetTop` is at or before `scrollTop + threshold`).
 *
 * @param headings     Headings with ratios populated
 * @param contentEl    The rendered markdown container
 * @param scrollTop    Current scroll offset of the scroll container
 * @param threshold    px below the top of the viewport to consider "passed" (default 100)
 */
export function getActiveHeadingId(
  headings: DocHeading[],
  contentEl: HTMLElement,
  scrollTop: number,
  threshold = 100
): string | null {
  let active: string | null = headings[0]?.id ?? null
  for (const h of headings) {
    const el = contentEl.querySelector(`[id="${h.id}"]`) as HTMLElement | null
    if (el && el.offsetTop <= scrollTop + threshold) {
      active = h.id
    }
  }
  return active
}
